import { rem } from 'polished'
import React, { useCallback, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import styled from 'styled-components'

import { required, urlRegisterConfig } from './Fields/config'
import RHFDatePickerInput from './Fields/RHFDatePickerInput'
import Input from './Fields/RHFInput'
import TextArea from './Fields/RHFTextArea'
import TimeInput from './Fields/TimeInput'
import { useDefaultDatePickerMonthYear } from './Hooks/useDefaultDates'
import { CreateNoteItemViewModel } from '../Create/models'
import { UpdateNoteItemViewModel } from '../Edit/models'

import AutoTooltipContent from 'components/Common/AutoTooltip/AutoTooltipContent'
import InlineGroup from 'components/Common/InlineGroup/index'
import LineInfoCircleIcon from 'components/Luxkit/Icons/line/LineInfoCircleIcon'
import { useAppSelector } from 'hooks/reduxHooks'
import { isLEStaff } from 'selectors/accountSelectors'
import CheckboxField from 'tripPlanner/components/TripModal/TripItems/Common/Fields/CheckboxField'

const SectionHeader = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${rem(8)};
  align-items: center;
  margin-bottom: ${rem(24)};
`

const StyledCheckboxField = styled(CheckboxField)`
  margin-bottom: 0;
`
const RelativeBlock = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`

interface Props {
  tripId: string
}

const NoteFields = ({ tripId }: Props) => {
  const { register, watch } = useFormContext<
    CreateNoteItemViewModel | UpdateNoteItemViewModel
  >()

  const isLEStaffMember = useAppSelector(isLEStaff)

  const [isTooltipVisible, setVisibleTooltip] = useState(false)

  const { defaultMonth, defaultYear } = useDefaultDatePickerMonthYear({
    tripId,
  })

  const onHover = useCallback(() => {
    setVisibleTooltip((prevState) => !prevState)
  }, [])

  useEffect(() => {
    return window.removeEventListener('hover', onHover)
  }, [onHover])

  const isSectionHeaderSelected = watch('isSectionHeader')

  return (
    <>
      {isLEStaffMember && (
        <SectionHeader>
          <StyledCheckboxField
            {...register('isSectionHeader')}
            label="Section Header"
          />
          <RelativeBlock>
            <LineInfoCircleIcon
              size="XS"
              onMouseLeave={onHover}
              onMouseEnter={onHover}
            />
            <AutoTooltipContent
              width={204}
              position="below"
              type="dark"
              open={isTooltipVisible}
            >
              This is intended for use in Curated Trips. If this box is ticked,
              this note will appear as a section header above all items that
              start on the same day.
            </AutoTooltipContent>
          </RelativeBlock>
        </SectionHeader>
      )}
      <Input
        {...register('title', { required })}
        required
        label="Title"
        placeholder="Title"
      />

      <TextArea
        {...register('notes', { required })}
        required
        label="Note"
        placeholder="Add notes"
        resize="vertical"
        rows={4}
      />

      <InlineGroup equal horizontal="between" spacing="lg">
        {/* @ts-ignore default values for props of class component */}
        <RHFDatePickerInput
          name="startDate"
          label="Start date"
          required={isSectionHeaderSelected}
          defaultMonth={defaultMonth}
          defaultYear={defaultYear}
        />
        <TimeInput
          {...register('startTime')}
          label="Time"
          placeholder="Select time"
        />
      </InlineGroup>

      <InlineGroup equal horizontal="between" spacing="lg">
        {/* @ts-ignore default values for props of class component */}
        <RHFDatePickerInput
          name="endDate"
          label="End date"
          defaultMonth={defaultMonth}
          defaultYear={defaultYear}
        />
        <TimeInput
          {...register('endTime')}
          label="Time"
          placeholder="Select time"
        />
      </InlineGroup>

      <Input
        {...register('link', { ...urlRegisterConfig })}
        label="Link"
        placeholder="Add website link"
      />
    </>
  )
}

export default NoteFields
